import { Header } from "@/components/Header";
import { FilterBar } from "@/components/FilterBar";
import { BrandScroller } from "@/components/BrandScroller";
import { CarCard } from "@/components/CarCard";
import { CarCardSkeleton } from "@/components/CarCardSkeleton";
import { Footer } from "@/components/Footer";
// Below-the-fold sections are lazy-loaded to keep the initial mobile bundle small.
import { SEOHead } from "@/components/SEOHead";
import { SITE_URL } from "@/lib/site";
import { useLanguage, translations } from "@/contexts/LanguageContext";
import { useFavorites } from "@/contexts/FavoritesContext";
import { fetchListingDetails, useInfiniteListings } from "@/hooks/useInfiniteListings";
import { useState, useEffect, useMemo, useRef, lazy, Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Sparkles, XCircle, Play } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";
import { useBatchImpressionTracker } from "@/hooks/useBatchImpressionTracker";

// Lazy load heavy components that aren't needed on initial render
const CarDetailModal = lazy(() => import("@/components/CarDetailModal").then((m) => ({ default: m.CarDetailModal })));
const ComparisonBar = lazy(() => import("@/components/ComparisonBar").then((m) => ({ default: m.ComparisonBar })));
const SEOTextBlock = lazy(() => import("@/components/SEOTextBlock").then((m) => ({ default: m.SEOTextBlock })));
const CarReels = lazy(() => import("@/components/CarReels"));
const SelfCarCheck = lazy(() => import("@/components/SelfCarCheck"));
const LowMileageScroller = lazy(() => import("@/components/LowMileageScroller").then((m) => ({ default: m.LowMileageScroller })));
const FinancePromoSection = lazy(() => import("@/components/FinancePromoSection").then((m) => ({ default: m.FinancePromoSection })));
const HowItWorksSection = lazy(() => import("@/components/HowItWorksSection").then((m) => ({ default: m.HowItWorksSection })));
const FAQSection = lazy(() => import("@/components/FAQSection").then((m) => ({ default: m.FAQSection })));
import { SelfCarCheckFAB } from "@/components/SelfCarCheckFAB";

const Index = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const { favorites } = useFavorites();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    location: 'all',
    brand: 'all',
    priceRange: 'all',
    sortBy: 'default',
    searchTerm: ''
  });

  // Use infinite scroll hook with server-side sorting
  const { listings, loading, loadingMore, hasMore, fetchMore } = useInfiniteListings({ sortBy: filters.sortBy });
  const isMobile = useIsMobile();
  const { observe: batchObserve, unobserve: batchUnobserve } = useBatchImpressionTracker();



  const [selectedCar, setSelectedCar] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [openRequestOnOpen, setOpenRequestOnOpen] = useState(false);

  // Default to 2-column grid on mobile
  const [isTwoColumn, setIsTwoColumn] = useState(true);
  const [isCompareMode, setIsCompareMode] = useState(false);
  const [isFavoritesMode, setIsFavoritesMode] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [matchedCriteria, setMatchedCriteria] = useState<string[]>([]);
  const [smartSearchIds, setSmartSearchIds] = useState<string[] | null>(null);
  const [reelsOpen, setReelsOpen] = useState(false);
  const [reelsStartIndex, setReelsStartIndex] = useState(0);
  const [carCheckOpen, setCarCheckOpen] = useState(false);
  const [isFilterSticky, setIsFilterSticky] = useState(false);
  const [showDeferredSections, setShowDeferredSections] = useState(false);
  const filterSentinelRef = useRef<HTMLDivElement>(null);
  const [headerHidden, setHeaderHidden] = useState(false);
  const carSectionRef = useRef<HTMLDivElement>(null);

  // Prefetch heavy interaction chunks while idle so clicks feel instant
  useEffect(() => {
    const prefetch = () => {
      import("@/components/CarDetailModal");
      import("@/components/CarReels");
      import("@/components/SelfCarCheck");
    };
    const w = window as typeof window & { requestIdleCallback?: (cb: () => void) => number };
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(prefetch);
      return () => (window as any).cancelIdleCallback?.(id);
    }
    const t = setTimeout(prefetch, 1200);
    return () => clearTimeout(t);
  }, []);

  // Auto hide/show sticky header on scroll — only after user scrolls past "All Cars" heading
  useEffect(() => {
    let lastY = window.scrollY;
    let accDown = 0;
    let accUp = 0;
    const onScroll = () => {
      const y = window.scrollY;
      const dy = y - lastY;
      const sectionTop = carSectionRef.current
        ? carSectionRef.current.getBoundingClientRect().top + window.scrollY - 100
        : Infinity;
      if (y < sectionTop) {
        setHeaderHidden(false);
        accDown = 0;
        accUp = 0;
      } else if (dy > 0) {
        accDown += dy;
        accUp = 0;
        if (accDown > 80) setHeaderHidden(true);
      } else if (dy < 0) {
        accUp += -dy;
        accDown = 0;
        if (accUp > 80) setHeaderHidden(false);
      }
      lastY = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Detect when the filter bar becomes sticky
  useEffect(() => {
    const element = filterSentinelRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsFilterSticky(!entry.isIntersecting),
      { threshold: 0, rootMargin: "-1px 0px 0px 0px" }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Delay below-the-fold sections so mobile can show listings first.
  useEffect(() => {
    if (loading) return;
    const run = () => setShowDeferredSections(true);
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const id = (window as any).requestIdleCallback(run, { timeout: 1500 });
      return () => (window as any).cancelIdleCallback?.(id);
    }
    const id = globalThis.setTimeout(run, 900);
    return () => globalThis.clearTimeout(id);
  }, [loading]);

  // Preload the first listing image for LCP optimization
  useEffect(() => {
    if (listings.length > 0 && listings[0].image_urls?.length > 0) {
      const firstImageUrl = listings[0].image_urls[0];
      // Check if preload link already exists
      if (!document.querySelector(`link[rel="preload"][href="${firstImageUrl}"]`)) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = firstImageUrl;
        link.fetchPriority = 'high';
        document.head.appendChild(link);
      }
    }
  }, [listings]);

  // Defer analytics initialization to after first paint using requestIdleCallback
  useEffect(() => {
    const initializeAnalytics = async () => {
      const { initSession, trackPageView, initScrollTracking } = await import("@/lib/analytics");
      await initSession();
      trackPageView();
      initScrollTracking();
    };

    // Use requestIdleCallback if available, otherwise setTimeout
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => initializeAnalytics());
    } else {
      setTimeout(initializeAnalytics, 100);
    }
  }, []);



  useEffect(() => {
    const carId = searchParams.get('car');
    if (carId && listings.length > 0) {
      const car = listings.find((c) => c.id === carId);
      if (car) {
        setSelectedCar(car);
        setModalOpen(true);
        searchParams.delete('car');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, listings, setSearchParams]);

  const scrollToCarSection = () => {
    carSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.location !== 'all') count++;
    if (filters.brand !== 'all') count++;
    if (filters.priceRange !== 'all') count++;
    if (filters.sortBy !== 'default') count++;
    if (filters.searchTerm !== '') count++;
    if (smartSearchIds !== null) count++;
    return count;
  };

  // Memoized filtered listings for performance
  const filteredListings = useMemo(() => {
    let filtered = [...listings];

    // Location filter (filter by district)
    if (filters.location !== 'all') {
      const locationToDistricts: {[key: string]: string[];} = {
        'bangkok': ['Bangkok'],
        'nakhon-pathom': ['Nakhon Pathom'],
        'taling-chan': ['Taling Chan'],
        'kaset-nawamin': ['Kaset-Nawamin', 'Kaset Nawamin'],
        'kanchanaphisek': ['Kanchanaphisek'],
        'latphrao': ['LatPhrao', 'Lat Phrao'],
        'ratchaphruek': ['Ratchaphruek'],
        'chaengwatthana': ['ChaengWatthana', 'Chaeng Watthana'],
        'kanlapaphruek': ['Kanlapaphruek'],
        'pak-kret': ['Pak Kret']
      };
      const districts = locationToDistricts[filters.location] || [];
      filtered = filtered.filter((car) =>
      districts.some((district) => car.district?.toLowerCase().includes(district.toLowerCase()))
      );
    }

    // Brand filter
    if (filters.brand !== 'all') {
      filtered = filtered.filter((car) => car.make?.toLowerCase() === filters.brand.toLowerCase());
    }

    // Price filter
    if (filters.priceRange !== 'all') {
      const priceRanges: {[key: string]: {min: number;max: number;};} = {
        '800k-1m': { min: 800000, max: 1000000 },
        '1m-1.5m': { min: 1000000, max: 1500000 },
        '1.5m-2m': { min: 1500000, max: 2000000 },
        '2m-2.5m': { min: 2000000, max: 2500000 },
        '2.5m-3m': { min: 2500000, max: 3000000 }
      };
      const range = priceRanges[filters.priceRange];
      if (range) {
        filtered = filtered.filter((car) => car.price_thb >= range.min && car.price_thb <= range.max);
      }
    }

    // Search filter - supports hidden TS code search for admin
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase().trim();

      // Check if search term looks like a TS code (e.g., "TS000", "ts123")
      const tsPattern = /^ts\d{1,4}$/i;
      if (tsPattern.test(filters.searchTerm.trim())) {
        // Exact listing code match (case insensitive)
        filtered = filtered.filter((car) =>
        car.listing_code?.toLowerCase() === searchLower
        );
      } else {
        // Normal search behavior
        filtered = filtered.filter((car) =>
        car.make?.toLowerCase().includes(searchLower) ||
        car.model?.toLowerCase().includes(searchLower) ||
        car.trim?.toLowerCase().includes(searchLower) ||
        car.title?.toLowerCase().includes(searchLower)
        );
      }
    }

    // No client-side sorting - server handles sort order
    // This preserves consistent order as background batches load

    // Smart search filter - uses pre-computed result IDs
    if (smartSearchIds !== null) {
      filtered = filtered.filter((car) => smartSearchIds.includes(car.id));
    }

    // Favorites filter
    if (isFavoritesMode) {
      filtered = filtered.filter((car) => favorites.includes(car.id));
    }

    return filtered;
  }, [listings, filters, isFavoritesMode, favorites, smartSearchIds]);

  const brandCounts = useMemo(() => {
    return listings.reduce((acc, l) => {
      const key = l.make?.toLowerCase();
      if (key) acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }, [listings]);

  const handleLocationChange = async (value: string) => {
    setFilters((prev) => ({ ...prev, location: value }));
    const { trackFilter } = await import("@/lib/analytics");
    trackFilter('location', value);
    setTimeout(scrollToCarSection, 100);
  };

  const handlePriceChange = async (value: string) => {
    setFilters((prev) => ({ ...prev, priceRange: value }));
    const { trackFilter } = await import("@/lib/analytics");
    trackFilter('price', value);
    setTimeout(scrollToCarSection, 100);
  };

  const handleSortChange = async (value: string) => {
    setFilters((prev) => ({ ...prev, sortBy: value }));
    const { trackFilter } = await import("@/lib/analytics");
    trackFilter('sort', value);
  };

  const handleSearchChange = (value: string) => {
    setFilters((prev) => ({ ...prev, searchTerm: value }));
    if (!value.trim()) {
      setMatchedCriteria([]);
      setSmartSearchIds(null);
    } else {
      // Scroll to results when user starts typing in search
      setTimeout(scrollToCarSection, 200);
    }
  };

  const handleSmartSearch = async (query: string) => {
    if (!query.trim()) {
      setMatchedCriteria([]);
      setSmartSearchIds(null);
      return;
    }
    setIsSearching(true);
    await new Promise((resolve) => setTimeout(resolve, 250));

    const { parseSearchQuery, smartFilter, getMatchedCriteriaSummary } = await import("@/lib/smartSearch");
    const { trackSearch } = await import("@/lib/analytics");

    const parsed = parseSearchQuery(query);
    const results = smartFilter(listings, parsed);
    const criteria = getMatchedCriteriaSummary(results);
    setMatchedCriteria(criteria);

    // Store matched car IDs to filter the display
    if (results.length > 0) {
      setSmartSearchIds(results.map((r) => r.car.id));
    } else {
      setSmartSearchIds([]);
    }

    // Clear the text search filter so it doesn't conflict
    setFilters((prev) => ({ ...prev, searchTerm: '' }));
    trackSearch(query);
    setIsSearching(false);
    setTimeout(scrollToCarSection, 300);
  };

  const handleBrandChange = async (value: string) => {
    setFilters((prev) => ({ ...prev, brand: value }));
    const { trackFilter } = await import("@/lib/analytics");
    trackFilter('brand', value);
    if (value !== 'all') setTimeout(scrollToCarSection, 100);
  };

  const handleResetFilters = () => {
    setFilters({
      location: 'all',
      brand: 'all',
      priceRange: 'all',
      sortBy: 'default',
      searchTerm: ''
    });
    setMatchedCriteria([]);
    setSmartSearchIds(null);
  };

  const handleGridToggle = () => setIsTwoColumn(!isTwoColumn);
  const handleCompareToggle = () => setIsCompareMode(!isCompareMode);
  const handleFavoritesToggle = () => setIsFavoritesMode(!isFavoritesMode);

  const handleCarClick = (car: any) => {
    setSelectedCar(car);
    setOpenRequestOnOpen(false);
    setModalOpen(true);
    fetchListingDetails(car.id)
      .then((details) => {
        if (details) {
          setSelectedCar((prev: any) => (prev?.id === car.id ? { ...prev, ...details } : prev));
        }
      })
      .catch((err) => console.error("Failed to load listing details:", err));
  };

  const handleLineClick = (car: any) => {
    setSelectedCar(car);
    setOpenRequestOnOpen(true);
    setModalOpen(true);
    fetchListingDetails(car.id)
      .then((details) => {
        if (details) {
          setSelectedCar((prev: any) => (prev?.id === car.id ? { ...prev, ...details } : prev));
        }
      })
      .catch((err) => console.error("Failed to load listing details:", err));
  };

  // Enhanced structured data with Organization schema for better Google branding
  const homepageStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
    {
      "@type": "WebSite",
      "@language": "th",
      "name": "TransShift - รถมือสอง",
      "alternateName": [
      "รถมือสอง TransShift",
      "รถยุโรปมือสอง TransShift",
      "รถมือสองพรีเมียม กรุงเทพ",
      "รถยนต์มือสอง กรุงเทพ"],

      "url": SITE_URL,
      "description": "รถมือสอง กรุงเทพ รถยุโรปมือสอง BMW Mercedes Porsche มือสอง ราคาดี ไมล์แท้ เช็คราคารถมือสอง ซื้อขายรถมือสองคุณภาพ รับรอง MotorVerified",
      "keywords": "รถมือสอง, รถยุโรปมือสอง, รถมือสอง กรุงเทพ, BMW มือสอง, Mercedes มือสอง, Porsche มือสอง, เช็คราคารถมือสอง",
      "inLanguage": "th-TH",
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${SITE_URL}/?search={search_term_string}`,
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@type": "Organization",
      "name": "TransShift",
      "alternateName": ["รถมือสองพรีเมียม TransShift", "รถยุโรปมือสอง"],
      "url": SITE_URL,
      "logo": `${SITE_URL}/favicon.png`,
      "description": "รถมือสอง กรุงเทพ รถยุโรปมือสอง คัดสรรพรีเมียม MotorVerified",
      "areaServed": {
        "@type": "Country",
        "name": "Thailand",
        "alternateName": "ประเทศไทย"
      },
      "sameAs": [
      "https://www.facebook.com/transshift",
      "https://line.me/ti/p/@transshift",
      "https://www.instagram.com/transshift"],

      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "sales",
        "areaServed": "TH",
        "availableLanguage": ["Thai", "English"]
      }
    },
    {
      "@type": ["LocalBusiness", "AutoDealer"],
      "@id": `${SITE_URL}/#localbusiness`,
      "name": "TransShift รถมือสองพรีเมียม",
      "alternateName": "รถยุโรปมือสอง กรุงเทพ",
      "image": `${SITE_URL}/og-image.jpg`,
      "url": SITE_URL,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Bangkok",
        "addressRegion": "กรุงเทพมหานคร",
        "addressCountry": "TH"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 13.7563,
        "longitude": 100.5018
      },
      "areaServed": {
        "@type": "City",
        "name": "Bangkok",
        "alternateName": "กรุงเทพมหานคร"
      },
      "priceRange": "฿800,000 - ฿3,000,000",
      "currenciesAccepted": "THB",
      "paymentAccepted": "Cash, Bank Transfer, Financing",
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "09:00",
        "closes": "18:00"
      }
    },
    {
      "@type": "Product",
      "name": "รถมือสอง",
      "description": "รถมือสองคัดสรรพรีเมี่ยม รถยุโรปมือสอง ตรวจสอบคุณภาพแล้ว"
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
      {
        "@type": "Question",
        "name": "รถมือสอง กรุงเทพ ซื้อที่ไหนดี?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "TransShift คัดสรรรถมือสองคุณภาพ รับรอง MotorVerified ตรวจสอบไมล์แท้และประวัติเซอร์วิส ซื้อตรงจากดีลเลอร์ มีสต็อก 60+ คัน"
        }
      },
      {
        "@type": "Question",
        "name": "รถยุโรปมือสอง BMW Mercedes ราคาเท่าไหร่?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "รถยุโรปมือสอง BMW Mercedes Porsche เริ่มต้นที่ ฿800,000 - ฿3,000,000 ขึ้นอยู่กับรุ่นและปี"
        }
      },
      {
        "@type": "Question",
        "name": "รถมือสองที่ TransShift ตรวจสอบอะไรบ้าง?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "MotorVerified ตรวจสอบ: ไมล์แท้จากศูนย์บริการ, ประวัติเซอร์วิส, สภาพรถ, เอกสารครบถ้วน, ไม่มีประวัติชน หรือน้ำท่วม"
        }
      },
      {
        "@type": "Question",
        "name": "ซื้อรถมือสองผ่อนได้ไหม?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "ได้ครับ TransShift มีบริการจัดไฟแนนซ์ ดอกเบี้ยต่ำ อนุมัติเร็ว ติดต่อดีลเลอร์โดยตรงผ่าน LINE"
        }
      },
      {
        "@type": "Question",
        "name": "รถยุโรปมือสอง ดูแลยากไหม?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "รถยุโรปมือสองที่ TransShift ผ่านการตรวจสอบ MotorVerified มีประวัติเซอร์วิสครบ พร้อมคำแนะนำศูนย์บริการใกล้บ้าน"
        }
      },
      {
        "@type": "Question",
        "name": "รถมือสอง ดาวน์เท่าไหร่?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "ดาวน์เริ่มต้น 10-20% ขึ้นอยู่กับรุ่นรถและสถาบันการเงิน ติดต่อดีลเลอร์โดยตรงเพื่อรับข้อเสนอพิเศษ"
        }
      },
      {
        "@type": "Question",
        "name": "รถมือสอง กรุงเทพ ส่งต่างจังหวัดได้ไหม?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "ได้ครับ ดีลเลอร์หลายรายมีบริการจัดส่งทั่วประเทศ สอบถามเพิ่มเติมผ่าน LINE"
        }
      },
      {
        "@type": "Question",
        "name": "เช็คราคารถมือสอง ทำอย่างไร?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "TransShift แสดงราคาจริงจากดีลเลอร์โดยตรง ไม่มีค่าหัวคิว เปรียบเทียบราคาได้ง่าย กด LINE เพื่อต่อรองราคากับผู้ขาย"
        }
      }]

    },
    {
      "@type": "Review",
      "itemReviewed": {
        "@type": "LocalBusiness",
        "name": "TransShift"
      },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "5",
        "bestRating": "5"
      },
      "author": {
        "@type": "Person",
        "name": "คุณวิชัย"
      },
      "reviewBody": "ซื้อ BMW 320d จาก TransShift ตรวจสอบไมล์แท้ เซอร์วิสครบ ประทับใจมากครับ"
    },
    {
      "@type": "Review",
      "itemReviewed": {
        "@type": "LocalBusiness",
        "name": "TransShift"
      },
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "5",
        "bestRating": "5"
      },
      "author": {
        "@type": "Person",
        "name": "คุณสมศรี"
      },
      "reviewBody": "บริการดี รถคุณภาพ ราคายุติธรรม ไม่ต้องต่อราคาเยอะ แนะนำเลยค่ะ"
    }]

  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead structuredData={homepageStructuredData} />
      
      {/* Noscript content for non-JS crawlers with Thai keywords including รถยุโรปมือสอง */}
      <noscript>
        <div>
          <h1>รถมือสอง กรุงเทพ - รถยุโรปมือสอง - TransShift</h1>
          <h2>รถยุโรปมือสอง BMW Mercedes Porsche Audi Volvo มือสอง ราคาดี</h2>
          <p>
            รถมือสอง กรุงเทพ รถยุโรปมือสอง ค้นหารถมือสอง รถยนต์มือสอง 
            รถเก๋งมือสอง รถบ้าน ราคาถูก สภาพดี ในกรุงเทพและปริมณฑล |
            BMW มือสอง Mercedes มือสอง Porsche มือสอง Audi มือสอง Volvo มือสอง |
            BMW ราคา Mercedes ราคา เช็คราคารถมือสอง ราคารถยุโรปมือสอง |
            รถมือสองคุณภาพ รถหรูมือสอง MotorVerified ดูสต็อก 60+ คัน
          </p>
        </div>
      </noscript>
      
      <Header />
      
      {/* Sentinel for sticky detection - must be outside sticky container */}
      <div id="filter-sentinel" className="h-px w-full" aria-hidden="true" ref={filterSentinelRef} />
      
      <div
        className={cn(
          "sticky top-0 z-40 transition-transform duration-300 ease-out pt-2 pb-1",
          headerHidden && "-translate-y-[120%]"
        )}
      >
        <div className="container px-3 flex flex-col items-center gap-2">
          {isFilterSticky ? (
            /* Merged single pill when sticky: filters on top, brands on bottom */
            <div className={cn(
              "w-full transition-all duration-300 overflow-visible",
              "rounded-xl bg-secondary/70 dark:bg-secondary/50 backdrop-blur-xl border border-border/40"
            )}>
              <FilterBar
                onLocationChange={handleLocationChange}
                onBrandChange={handleBrandChange}
                onPriceChange={handlePriceChange}
                onSortChange={handleSortChange}
                onSearchChange={handleSearchChange}
                onSmartSearch={handleSmartSearch}
                onResetFilters={handleResetFilters}
                onGridToggle={handleGridToggle}
                isTwoColumn={isTwoColumn}
                onCompareToggle={handleCompareToggle}
                isCompareMode={isCompareMode}
                onFavoritesToggle={handleFavoritesToggle}
                isFavoritesMode={isFavoritesMode}
                favoritesCount={favorites.length}
                location={filters.location}
                brand={filters.brand}
                priceRange={filters.priceRange}
                sortBy={filters.sortBy}
                searchTerm={filters.searchTerm}
                activeFilterCount={getActiveFilterCount()}
                isSearching={isSearching}
                isSticky={isFilterSticky}
                merged={true}
              />
              <div className="h-px bg-border/50 w-full" />
              <BrandScroller
                selectedBrand={filters.brand}
                onBrandChange={handleBrandChange}
                brandCounts={brandCounts}
                merged={true}
              />
            </div>
          ) : (
            <>
              <div className={cn(
                "w-full transition-all duration-300 overflow-visible",
                "bg-transparent border border-transparent"
              )}>
                <FilterBar
                  onLocationChange={handleLocationChange}
                  onBrandChange={handleBrandChange}
                  onPriceChange={handlePriceChange}
                  onSortChange={handleSortChange}
                  onSearchChange={handleSearchChange}
                  onSmartSearch={handleSmartSearch}
                  onResetFilters={handleResetFilters}
                  onGridToggle={handleGridToggle}
                  isTwoColumn={isTwoColumn}
                  onCompareToggle={handleCompareToggle}
                  isCompareMode={isCompareMode}
                  onFavoritesToggle={handleFavoritesToggle}
                  isFavoritesMode={isFavoritesMode}
                  favoritesCount={favorites.length}
                  location={filters.location}
                  brand={filters.brand}
                  priceRange={filters.priceRange}
                  sortBy={filters.sortBy}
                  searchTerm={filters.searchTerm}
                  activeFilterCount={getActiveFilterCount()}
                  isSearching={isSearching}
                  isSticky={isFilterSticky}
                />
              </div>

              <div className={cn(
                "w-full transition-all duration-300 overflow-visible",
                "bg-transparent border border-transparent"
              )}>
                <BrandScroller
                  selectedBrand={filters.brand}
                  onBrandChange={handleBrandChange}
                  brandCounts={brandCounts}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Showroom Section */}
      {!loading && listings.length > 0

      }

      {/* Low Mileage Cars Section */}
      {showDeferredSections && !loading && listings.length > 0 &&
      <Suspense fallback={null}>
          <LowMileageScroller listings={listings} onCarClick={handleCarClick} onLineClick={handleLineClick} />
        </Suspense>
      }

      {showDeferredSections && <Suspense fallback={null}>
        <FinancePromoSection />
      </Suspense>}

      <main ref={carSectionRef} className="container px-4 md:py-12 flex-1 py-[4px]">
        {/* Search Summary / Active Filter Bar */}
        {(matchedCriteria.length > 0 || getActiveFilterCount() > 0) && !loading &&
        <div className="flex items-center gap-2 mb-6 animate-fade-in flex-wrap">
            {matchedCriteria.length > 0 &&
          <>
                <Sparkles className="h-4 w-4 text-primary shrink-0" />
                <span className="text-sm text-muted-foreground">
                  {language === 'th' ? 'พบ' : 'Found'} <span className="font-semibold text-foreground">{filteredListings.length}</span> {language === 'th' ? 'คัน' : 'matches'}
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {matchedCriteria.slice(0, 5).map((criterion, index) =>
              <Badge key={index} variant="secondary" className="text-xs">
                      {criterion}
                    </Badge>
              )}
                </div>
              </>
          }
            {getActiveFilterCount() > 0 &&
          <button
            onClick={handleResetFilters}
            className="ml-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors bg-muted hover:bg-muted/80 px-2.5 py-1 rounded-full border border-border">
            
                <XCircle className="h-3.5 w-3.5" />
                {language === 'th' ? 'ล้างตัวกรอง' : 'Clear filters'}
              </button>
          }
          </div>
        }

        {/* All Cars Title */}
        <div className="flex flex-col items-center gap-2 mb-8 mt-4">
          <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-foreground py-0 my-[5px]">
            {language === "th" ? "รถทั้งหมด" : "All Cars"}
          </h2>
          <div className="flex items-center gap-2">
            <div className="h-px w-8 bg-border" />
            <span className="text-sm text-muted-foreground bg-muted px-3 py-1 rounded-full font-medium">
              {filteredListings.length} {language === "th" ? "คัน" : "cars"}
            </span>
            <div className="h-px w-8 bg-border" />
          </div>
        </div>

        {loading ?
        <div className={`grid gap-4 md:gap-6 ${isTwoColumn ? 'grid-cols-2' : 'grid-cols-1'} sm:grid-cols-2 lg:grid-cols-3`}>
            {[...Array(6)].map((_, i) => <CarCardSkeleton key={i} />)}
          </div> :
        filteredListings.length === 0 ?
        <div className="flex min-h-[50vh] items-center justify-center">
            <p className="text-lg text-muted-foreground">
              {language === "en" ? "No cars match your filters." : "ไม่พบรถที่ตรงกับตัวกรอง"}
            </p>
          </div> :

        <>
          <div className={`grid gap-4 md:gap-6 ${isTwoColumn ? 'grid-cols-2' : 'grid-cols-1'} sm:grid-cols-2 lg:grid-cols-3`}>
            {filteredListings.map((car, index) => {
              const isLastItem = index === filteredListings.length - 1;
              const isOddLast = isLastItem && isTwoColumn && filteredListings.length % 2 !== 0;
              return (
                <div key={car.id} className={isOddLast ? 'col-span-2 sm:col-span-1 flex justify-center sm:block' : ''}>
                  <div className={isOddLast ? 'w-[calc(50%-0.5rem)] sm:w-full' : ''}>
                    <CarCard
                      title={car.title}
                      year={car.year}
                      make={car.make}
                      model={car.model}
                      trim={car.trim}
                      price_thb={car.price_thb}
                      old_price_thb={car.old_price_thb}
                      discount_price_thb={car.discount_price_thb}
                      discount_percent={car.discount_percent}
                      promo_event={car.promo_event}
                      promo_active={car.promo_active}
                      mileage_km={car.mileage_km}
                      district={car.district}
                      district_th={car.district_th}
                      image_urls={car.image_urls}
                      car={car}
                      onClick={() => handleCarClick(car)}
                      onLineClick={() => handleLineClick(car)}
                      showCompare={isCompareMode}
                      isTwoColumn={isTwoColumn}
                      index={index}
                      batchObserve={isMobile ? batchObserve : undefined}
                      batchUnobserve={isMobile ? batchUnobserve : undefined} />
                  </div>
                </div>);

            })}
          </div>
        </>
        }
      </main>


      {showDeferredSections && <Suspense fallback={null}>
        <SEOTextBlock />
      </Suspense>}
      <Suspense fallback={null}>
        <ComparisonBar />
      </Suspense>
      {selectedCar && <Suspense fallback={null}>
        <CarDetailModal
          open={modalOpen}
          onOpenChange={(open) => {
            setModalOpen(open);
            if (!open) setOpenRequestOnOpen(false);
          }}
          car={selectedCar}
          autoOpenRequestDialog={openRequestOnOpen} />
        
      </Suspense>}
      {showDeferredSections && <Suspense fallback={null}>
        <HowItWorksSection />
      </Suspense>}
      {showDeferredSections && <Suspense fallback={null}>
        <FAQSection />
      </Suspense>}
      <Footer />

      {/* Floating Spotlight button */}
      {!loading && filteredListings.length > 0 && !reelsOpen &&
      <button
        onClick={() => {setReelsStartIndex(0);setReelsOpen(true);}}
        className="fixed bottom-6 right-6 z-50 group flex items-center justify-center gap-1.5 w-[136px] h-10 rounded-full shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105 active:scale-95 border border-white/20"
        style={{
          background: 'linear-gradient(135deg, #e8e8e8 0%, #b0b0b0 25%, #d4d4d4 50%, #a0a0a0 75%, #c8c8c8 100%)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -1px 0 rgba(0,0,0,0.1)',
          bottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))'
        }}
        aria-label="Open Spotlight view">
        
          <Play className="h-4 w-4 fill-current text-gray-800 drop-shadow-sm" />
          <span className="text-sm font-bold text-gray-800 tracking-wide drop-shadow-sm">Spotlight</span>
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-white/0 via-white/40 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{ backgroundSize: '200% 100%', animation: 'shimmer 2s infinite' }} />
        </button>
      }

      {/* Floating Self Car Check button */}
      {!reelsOpen && !carCheckOpen && (
        <SelfCarCheckFAB onClick={() => setCarCheckOpen(true)} />
      )}

      <Suspense fallback={null}>
        <SelfCarCheck open={carCheckOpen} onOpenChange={setCarCheckOpen} />
      </Suspense>

      {/* Full-screen Reels */}
      {reelsOpen &&
      <Suspense fallback={<div className="fixed inset-0 z-[100] bg-black" />}>
          <CarReels
          cars={filteredListings.map((car) => ({
            id: car.id,
            title: car.title,
            year: car.year,
            make: car.make,
            model: car.model,
            trim: car.trim,
            price_thb: car.price_thb,
            mileage_km: car.mileage_km,
            district: car.district,
            district_th: car.district_th,
            image_urls: car.image_urls,
            line_id: car.line_id,
            contact_phone: car.contact_phone
          }))}
          startIndex={reelsStartIndex}
          onClose={() => setReelsOpen(false)}
          onCarClick={(reelCar) => {
            setReelsOpen(false);
            const fullCar = filteredListings.find((c) => c.id === reelCar.id);
            if (fullCar) handleCarClick(fullCar);
          }}
          onLineClick={(reelCar) => {
            setReelsOpen(false);
            const fullCar = filteredListings.find((c) => c.id === reelCar.id);
            if (fullCar) handleLineClick(fullCar);
          }} />
        
        </Suspense>
      }
    </div>);

};

export default Index;